import { FC, ReactNode, useMemo, useState } from 'react'

import { styled } from 'styled-components'

import CopyButton from 'core/components/lib/CopyButton'
import {
  onSurfaceDisabled,
  onSurfaceVariant,
  outline,
  surface,
  syntax,
} from 'core/styles'

import { JsonLine, TokenKind, linesToText, toJsonLines } from './toJsonLines'

/**
 * Above this serialized size JsonView opens folded so the tab stays
 * responsive. Copy is unaffected at any size.
 */
export const LARGE_PAYLOAD_BYTES = 200_000

// Used when no ThemeProvider is mounted, matching the pattern in Table.tsx.
// `syntax` has more keys than TokenKind (container/currency are Form
// View-only), which is fine here since only TokenKind's keys are ever read.
const FALLBACK: { [kind in TokenKind]: string } = syntax

type JsonViewProps = {
  className?: string
  /** The value to render. Passed through untouched. */
  value: unknown
  /** Folds every container at or below this depth on first render. */
  initialCollapseDepth?: number
}

const foldPathsFromDepth = (
  lines: Array<JsonLine>,
  depth: number,
): Set<string> => {
  const paths = new Set<string>()

  lines.forEach((line) => {
    if (line.fold && line.depth >= depth) paths.add(line.fold.path)
  })

  return paths
}

/** A read-only, syntax-highlighted view of a value as pretty-printed JSON. */
const JsonView: FC<JsonViewProps> = ({
  className,
  value,
  initialCollapseDepth,
}) => {
  const lines = useMemo(() => toJsonLines(value), [value])
  const text = useMemo(() => linesToText(lines), [lines])
  const isLarge = text.length > LARGE_PAYLOAD_BYTES

  // Fold state deliberately survives a change of `value`: saving a record
  // should not re-collapse what the reader opened. Stale paths simply never
  // match and are harmless.
  const [folded, setFolded] = useState<Set<string>>(() => {
    const depth = initialCollapseDepth ?? (isLarge ? 1 : undefined)
    return depth === undefined ? new Set() : foldPathsFromDepth(lines, depth)
  })

  const toggle = (path: string) =>
    setFolded((current) => {
      const next = new Set(current)
      if (!next.delete(path)) next.add(path)
      return next
    })

  const rows: Array<ReactNode> = []

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    const { fold } = line
    const isFolded = fold !== undefined && folded.has(fold.path)
    const tokens = isFolded && fold ? fold.collapsedTokens : line.tokens

    rows.push(
      <Row key={index}>
        <Gutter>
          {fold ? (
            <FoldToggle
              aria-expanded={!isFolded}
              aria-label={`${isFolded ? 'Expand' : 'Collapse'} ${fold.path}`}
              onClick={() => toggle(fold.path)}
              type='button'
            >
              {isFolded ? '▸' : '▾'}
            </FoldToggle>
          ) : null}
        </Gutter>
        <Content>
          {tokens.map((token, tokenIndex) => (
            <Token $kind={token.kind} key={tokenIndex}>
              {token.text}
            </Token>
          ))}
        </Content>
      </Row>,
    )

    if (isFolded && fold) index = fold.endIndex
  }

  return (
    <Wrapper className={className}>
      <Header>
        <Banner>
          {isLarge
            ? 'Large response — collapsed for speed. Copy returns everything.'
            : null}
        </Banner>
        <CopyButton label='Copy JSON' text={() => text} />
      </Header>
      <Code>{rows}</Code>
    </Wrapper>
  )
}

export default JsonView

const Wrapper = styled.div`
  border: 1px solid ${(p) => p.theme.border ?? outline};
  border-radius: 6px;
  background-color: ${(p) => p.theme.offset ?? surface};
`

const Header = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.hairline ?? outline};
  padding: 4px 8px;
`

const Banner = styled.span`
  color: ${(p) => p.theme.labelText ?? onSurfaceVariant};
  font-size: 12px;
`

const Code = styled.div`
  padding: 8px 0;
  overflow-x: auto;
  line-height: 1.55;
  font-family: 'SF Mono', 'JetBrains Mono', Menlo, Consolas, monospace;
  font-size: 12px;
`

const Row = styled.div`
  display: flex;
  white-space: pre;
`

const Gutter = styled.div`
  flex: 0 0 16px;
  text-align: center;

  /* Keeps fold glyphs out of a manual text selection. */
  user-select: none;
`

const FoldToggle = styled.button`
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${(p) => p.theme.disabledText ?? onSurfaceDisabled};
  font: inherit;
`

const Content = styled.div`
  flex: 1 1 auto;
`

const Token = styled.span<{ $kind: TokenKind }>`
  color: ${(p) => p.theme.syntax?.[p.$kind] ?? FALLBACK[p.$kind]};
`
